/**
 * Unified pathway-difference cost model.
 * All cost views use the same formulas; only baseline and policy pathways differ.
 * Cost = CAPEX (electrification, reach, EV adoption) − operating cost savings (energy/fuel).
 */

import type { BuildingsCostAssumptions } from './buildingsCostModel';
import { getAssumptions as getBuildingsAssumptions, REACH_CODE_REBATE } from './buildingsCostModel';
import { COMMERCIAL_ANNUAL_GROWTH_RATE } from './buildingsEfficiencyModel';
import type { TransportCostOptions } from './transportCostModel';
import {
  GASOLINE_PRICE_PER_GALLON_DEFAULT,
  EV_KWH_PER_MILE,
  ICE_MPG,
  EV_INCREMENTAL_COST,
  CHARGER_COST_PER_EV,
  DISCOUNT_RATE as TRANSPORT_DISCOUNT_RATE,
} from './transportCostModel';

const BASE_YEAR = 2023;
const TARGET_YEAR = 2045;
const ANALYSIS_YEAR_MIN = 2023;
const ANALYSIS_YEAR_MAX = 2045;
const AVG_MILES_PER_VEHICLE_PER_YEAR = 12000;

function ramp(year: number, base: number, target: number): number {
  if (year <= base) return 0;
  if (year >= target) return 1;
  return (year - base) / (target - base);
}

function newResShare(year: number): number {
  if (year <= BASE_YEAR) return 0;
  const growth = 0.02;
  const unitsAtYear = Math.pow(1 + growth, year - BASE_YEAR);
  const existing = Math.max(0, Math.min(1, 1 / unitsAtYear));
  return 1 - existing;
}

function newComShare(year: number): number {
  if (year <= BASE_YEAR) return 0;
  const stockIndex = Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, year - BASE_YEAR);
  return Math.max(0, 1 - 1 / stockIndex);
}

// ============== BUILDINGS ==============

export interface BuildingsPathway {
  years: number[];
  thermsByYear: number[];
  kwhByYear: number[];
  /** Residential reach code % (0–100). Optional; 0 if omitted. */
  resReachCodePercent?: number;
  /** Commercial reach code % (0–100). Optional; 0 if omitted. */
  comReachCodePercent?: number;
}

/** Existing-building retrofit policy: linear ramp 2023→2045 on 2023 housing/establishment stock (matches efficiency layer). */
export interface BuildingsRetrofitContext {
  housingUnits2023: number;
  establishments2023: number;
  /** Cumulative % of 2023 stock addressed by 2045, residential. */
  residentialRetrofitPercent2045: number;
  commercialRetrofitPercent2045: number;
}

/** Context for building electrification CAPEX ($/unit). When provided, replaces $/therm-based electrification CAPEX. */
export interface BuildingsElectrificationContext {
  housingUnitsByYear: Record<number, number>;
  baselineResThermsByYear: number[] | Record<number, number>;
  policyResThermsByYear: number[] | Record<number, number>;
  baselineComThermsByYear: number[] | Record<number, number>;
  policyComThermsByYear: number[] | Record<number, number>;
  resHeatPumpIncrementalCostPerUnit: number;
  comHeatPumpIncrementalCostPerUnit: number;
  avgThermsPerResUnit: number;
  avgThermsPerComEquivalentUnit: number;
  heatPumpRebate: number;
  /** When false, therm reduction is from efficiency only (no heat pumps) → electrification CAPEX = 0. */
  hasElectrification?: boolean;
}

/** Per-year electrification debug (for validation, e.g. Davis). */
export interface ElectrificationDebugByYear {
  year: number;
  housingUnitsBau: number;
  baselineResTherms: number;
  policyResTherms: number;
  convertedResUnits: number;
  electrifiedResUnitsCumulative: number;
  annualResElectrificationCapex: number;
  baselineComTherms: number;
  policyComTherms: number;
  convertedComUnits: number;
  electrifiedComUnitsCumulative: number;
  annualComElectrificationCapex: number;
}

/** Cumulative modeled electrified stock at end of analysis year (2045) — used for bill-impact “per affected home” metrics. */
export interface ElectrificationStock2045 {
  cumulativeElectrifiedResidentialUnits: number;
  cumulativeElectrifiedCommercialEquivalentUnits: number;
}

export interface BuildingsPathwayCostResult {
  upfrontCapitalCost: number;
  annualSavingsSeries: { year: number; value: number }[];
  annualCapexSeries: { year: number; value: number }[];
  cumulativeNetCostSeries: { year: number; value: number }[];
  cumulativeNetCost: number;
  netPresentValue: number;
  costPerTon: number | null;
  paybackYear: number | null;
  totalBuildingEmissionsReductionMT: number;
  baselineCostByYear: { year: number; value: number }[];
  policyCostByYear: { year: number; value: number }[];
  /** BAU vs scenario cost by fuel (for electricity vs gas comparison chart). */
  baselineGasCostByYear: { year: number; value: number }[];
  baselineElecCostByYear: { year: number; value: number }[];
  policyGasCostByYear: { year: number; value: number }[];
  policyElecCostByYear: { year: number; value: number }[];
  electrificationCapex: number;
  reachCapex: number;
  residentialRetrofitCapex: number;
  commercialRetrofitCapex: number;
  /** Electrification $/unit debug when electrificationContext provided and includeElectrificationDebug. */
  electrificationDebug?: {
    byYear: ElectrificationDebugByYear[];
    year2045: {
      cumulativeConvertedResUnits: number;
      cumulativeConvertedComUnits: number;
      totalElectrificationCapex: number;
    };
  };
  /** Per-location audit: baseline gas cost, policy gas/elec cost, incremental cost, electricity added (kWh), effective $/therm electric. */
  operatingCostDebug?: {
    baselineGasCostTotal: number;
    policyGasCostTotal: number;
    policyElecCostTotal: number;
    incrementalOperatingCostTotal: number;
    electricityAddedKwhTotal: number;
    effectiveDollarsPerThermElectric: number;
  };
  /** Present when electrification context exists; cumulative stock at 2045 for residential/commercial equivalent units. */
  electrificationStock2045?: ElectrificationStock2045;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/**
 * Compute buildings cost between two pathways using a single formula:
 * - Operating savings = baseline energy cost − policy energy cost (therms * gasPrice + kWh * elecPrice)
 * - CAPEX electrification = convertedResUnits × resCost + convertedComUnits × comCost ($/unit) when electrificationContext provided; else $/therm (deprecated)
 * - CAPEX reach = (policy reach % − baseline reach %) × new units proxy × cost per unit
 * - Annual net cost = capex − operating savings; then cumulative, NPV, payback.
 */
export function computeBuildingsCostBetweenPathways(
  baselinePathway: BuildingsPathway,
  policyPathway: BuildingsPathway,
  assumptions?: BuildingsCostAssumptions,
  electrificationContext?: BuildingsElectrificationContext,
  includeElectrificationDebug?: boolean,
  retrofitContext?: BuildingsRetrofitContext
): BuildingsPathwayCostResult {
  const a = getBuildingsAssumptions(assumptions);
  const years = baselinePathway.years.filter((y) => policyPathway.years.includes(y));
  if (years.length === 0) {
    return {
      upfrontCapitalCost: 0,
      annualSavingsSeries: [],
      annualCapexSeries: [],
      cumulativeNetCostSeries: [],
      cumulativeNetCost: 0,
      netPresentValue: 0,
      costPerTon: null,
      paybackYear: null,
      totalBuildingEmissionsReductionMT: 0,
      baselineCostByYear: [],
      policyCostByYear: [],
      baselineGasCostByYear: [],
      baselineElecCostByYear: [],
      policyGasCostByYear: [],
      policyElecCostByYear: [],
      electrificationCapex: 0,
      reachCapex: 0,
      residentialRetrofitCapex: 0,
      commercialRetrofitCapex: 0,
      electrificationStock2045: undefined,
    };
  }

  const baselineTherms = years.map((y) => baselinePathway.thermsByYear[baselinePathway.years.indexOf(y)] ?? 0);
  const baselineKwh = years.map((y) => baselinePathway.kwhByYear[baselinePathway.years.indexOf(y)] ?? 0);
  const policyTherms = years.map((y) => policyPathway.thermsByYear[policyPathway.years.indexOf(y)] ?? 0);
  const policyKwh = years.map((y) => policyPathway.kwhByYear[policyPathway.years.indexOf(y)] ?? 0);

  // A. Operating costs: baseline_energy_cost = baseline_gas_therms*gas_price + baseline_kwh*elec_price; policy_energy_cost = same for policy.
  // incremental_operating_cost = policy_energy_cost - baseline_energy_cost. Savings = baseline - policy (so negative savings = operating cost increase).
  const baselineCostByYear = years.map((_, i) =>
    Math.max(0, baselineTherms[i] * a.naturalGasPricePerTherm + baselineKwh[i] * a.electricityPricePerKwh)
  );
  const policyCostByYear = years.map((_, i) =>
    Math.max(0, policyTherms[i] * a.naturalGasPricePerTherm + policyKwh[i] * a.electricityPricePerKwh)
  );
  const annualSavingsSeries = years.map((y, i) => ({
    year: y,
    value: baselineCostByYear[i] - policyCostByYear[i],
  }));

  // Note: a naive $/kWh vs $/therm COP check is misleading here — policy pathway bundles efficiency,
  // therm reductions, and stock turnover with electrification. Optional deep checks: REACT_APP_DEBUG_PATHWAY_SANITY=1.
  if (
    typeof process !== 'undefined' &&
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_DEBUG_PATHWAY_SANITY === '1' &&
    electrificationContext?.hasElectrification &&
    typeof window !== 'undefined'
  ) {
    const KWH_PER_THERM = 29.3;
    const copForCheck = 3.0;
    const breakEvenElecPerTherm = a.naturalGasPricePerTherm / (KWH_PER_THERM / copForCheck);
    if (a.electricityPricePerKwh > breakEvenElecPerTherm) {
      const totalSavings = annualSavingsSeries.reduce((s, r) => s + r.value, 0);
      if (totalSavings > 0) {
        // expected when efficiency dominates price disadvantage
      }
    }
  }

  // B. CAPEX: electrification ($/unit or deprecated $/therm)
  // When hasElectrification is false, therm reduction is from efficiency only → no electrification CAPEX.
  let electrificationCapexByYear: number[];
  let electrificationDebugResult: BuildingsPathwayCostResult['electrificationDebug'];
  let electrificationStock2045: ElectrificationStock2045 | undefined;
  if (electrificationContext?.hasElectrification === false) {
    electrificationCapexByYear = years.map(() => 0);
    electrificationStock2045 = {
      cumulativeElectrifiedResidentialUnits: 0,
      cumulativeElectrifiedCommercialEquivalentUnits: 0,
    };
  } else if (electrificationContext) {
    const ctx = electrificationContext;
    const resCost = Math.max(0, ctx.resHeatPumpIncrementalCostPerUnit - ctx.heatPumpRebate);
    const comCost = Math.max(0, ctx.comHeatPumpIncrementalCostPerUnit - ctx.heatPumpRebate);
    const avgCom = Math.max(1, ctx.avgThermsPerComEquivalentUnit);

    const electrifiedResUnits: number[] = [];
    const electrifiedComUnits: number[] = [];
    for (let i = 0; i < years.length; i++) {
      const y = years[i];
      const bRes = ctx.baselineResThermsByYear[y] ?? 0;
      const pRes = ctx.policyResThermsByYear[y] ?? 0;
      const bCom = ctx.baselineComThermsByYear[y] ?? 0;
      const pCom = ctx.policyComThermsByYear[y] ?? 0;

      const resThermsDisplaced = Math.max(0, bRes - pRes);
      const electrifiedResShare = clamp(bRes > 0 ? resThermsDisplaced / bRes : 0, 0, 1);
      const housingUnitsBau = ctx.housingUnitsByYear[y] ?? 0;
      electrifiedResUnits.push(housingUnitsBau * electrifiedResShare);

      const baselineComUnits = bCom > 0 ? bCom / avgCom : 0;
      const comThermsDisplaced = Math.max(0, bCom - pCom);
      const electrifiedComShare = clamp(bCom > 0 ? comThermsDisplaced / bCom : 0, 0, 1);
      electrifiedComUnits.push(baselineComUnits * electrifiedComShare);
    }

    electrificationCapexByYear = years.map((_, i) => {
      const convertedRes = i === 0
        ? electrifiedResUnits[0]
        : Math.max(0, electrifiedResUnits[i] - electrifiedResUnits[i - 1]);
      const convertedCom = i === 0
        ? electrifiedComUnits[0]
        : Math.max(0, electrifiedComUnits[i] - electrifiedComUnits[i - 1]);
      return convertedRes * resCost + convertedCom * comCost;
    });
    const dbgIdx2045 = years.indexOf(TARGET_YEAR);
    if (includeElectrificationDebug) {
      const byYear: ElectrificationDebugByYear[] = years.map((y, i) => {
        const convertedRes = i === 0 ? electrifiedResUnits[0] : Math.max(0, electrifiedResUnits[i] - electrifiedResUnits[i - 1]);
        const convertedCom = i === 0 ? electrifiedComUnits[0] : Math.max(0, electrifiedComUnits[i] - electrifiedComUnits[i - 1]);
        return {
          year: y,
          housingUnitsBau: ctx.housingUnitsByYear[y] ?? 0,
          baselineResTherms: ctx.baselineResThermsByYear[y] ?? 0,
          policyResTherms: ctx.policyResThermsByYear[y] ?? 0,
          convertedResUnits: convertedRes,
          electrifiedResUnitsCumulative: electrifiedResUnits[i],
          annualResElectrificationCapex: convertedRes * resCost,
          baselineComTherms: ctx.baselineComThermsByYear[y] ?? 0,
          policyComTherms: ctx.policyComThermsByYear[y] ?? 0,
          convertedComUnits: convertedCom,
          electrifiedComUnitsCumulative: electrifiedComUnits[i],
          annualComElectrificationCapex: convertedCom * comCost,
        };
      });
      electrificationDebugResult = {
        byYear,
        year2045: {
          cumulativeConvertedResUnits: electrifiedResUnits[dbgIdx2045] ?? electrifiedResUnits[electrifiedResUnits.length - 1] ?? 0,
          cumulativeConvertedComUnits: electrifiedComUnits[dbgIdx2045] ?? electrifiedComUnits[electrifiedComUnits.length - 1] ?? 0,
          totalElectrificationCapex: electrificationCapexByYear.reduce((a, b) => a + b, 0),
        },
      };
    }
    const stockIdx = years.indexOf(TARGET_YEAR);
    const stockPos = stockIdx >= 0 ? stockIdx : Math.max(0, years.length - 1);
    electrificationStock2045 = {
      cumulativeElectrifiedResidentialUnits: electrifiedResUnits[stockPos] ?? 0,
      cumulativeElectrifiedCommercialEquivalentUnits: electrifiedComUnits[stockPos] ?? 0,
    };
  } else {
    // No electrification context: no electrification CAPEX (e.g. efficiency-only scenario).
    electrificationCapexByYear = years.map(() => 0);
    electrificationStock2045 = undefined;
  }

  // Reach: (policy − baseline) reach % × new units proxy × cost
  const baselineResReach = baselinePathway.resReachCodePercent ?? 0;
  const baselineComReach = baselinePathway.comReachCodePercent ?? 0;
  const policyResReach = policyPathway.resReachCodePercent ?? 0;
  const policyComReach = policyPathway.comReachCodePercent ?? 0;
  const reachCapexByYear = years.map((year, i) => {
    const r = ramp(year, BASE_YEAR, TARGET_YEAR);
    const newRes = 2000 * newResShare(year) * r;
    const newCom = 500 * newComShare(year) * r;
    const resDelta = Math.max(0, (policyResReach - baselineResReach) / 100 * newRes * (a.resReachIncrementalCostPerUnit - REACH_CODE_REBATE));
    const comDelta = Math.max(0, (policyComReach - baselineComReach) / 100 * newCom * (a.comReachIncrementalCostPerUnit - REACH_CODE_REBATE));
    return resDelta + comDelta;
  });

  const netResRetroUnit = Math.max(0, a.weatherizationCostPerHome - a.weatherizationRebate);
  const netComRetroUnit = Math.max(0, a.commercialRetrofitCostPerEstablishment - a.commercialRetrofitRebate);

  let resRetroCapexByYear: number[];
  let comRetroCapexByYear: number[];
  if (
    retrofitContext &&
    (retrofitContext.housingUnits2023 > 0 || retrofitContext.establishments2023 > 0) &&
    (retrofitContext.residentialRetrofitPercent2045 > 0 || retrofitContext.commercialRetrofitPercent2045 > 0)
  ) {
    const U0 = Math.max(0, retrofitContext.housingUnits2023);
    const E0 = Math.max(0, retrofitContext.establishments2023);
    const pRes = clamp(retrofitContext.residentialRetrofitPercent2045 / 100, 0, 1);
    const pCom = clamp(retrofitContext.commercialRetrofitPercent2045 / 100, 0, 1);
    resRetroCapexByYear = years.map((year, i) => {
      const rPrev = i === 0 ? 0 : ramp(years[i - 1], BASE_YEAR, TARGET_YEAR);
      const rCur = ramp(year, BASE_YEAR, TARGET_YEAR);
      const d = Math.max(0, rCur - rPrev);
      return U0 * pRes * d * netResRetroUnit;
    });
    comRetroCapexByYear = years.map((year, i) => {
      const rPrev = i === 0 ? 0 : ramp(years[i - 1], BASE_YEAR, TARGET_YEAR);
      const rCur = ramp(year, BASE_YEAR, TARGET_YEAR);
      const d = Math.max(0, rCur - rPrev);
      return E0 * pCom * d * netComRetroUnit;
    });
  } else {
    resRetroCapexByYear = years.map(() => 0);
    comRetroCapexByYear = years.map(() => 0);
  }

  const annualCapexSeries = years.map((y, i) => ({
    year: y,
    value:
      electrificationCapexByYear[i] +
      reachCapexByYear[i] +
      resRetroCapexByYear[i] +
      comRetroCapexByYear[i],
  }));

  const upfrontCapitalCost = annualCapexSeries.reduce((sum, row) => sum + row.value, 0);

  // C. Annual net cost = capex − savings; cumulative
  let cumulative = 0;
  const cumulativeNetCostSeries = years.map((y, i) => {
    const netCost = annualCapexSeries[i].value - annualSavingsSeries[i].value;
    cumulative += netCost;
    return { year: y, value: cumulative };
  });
  const idx2045 = years.indexOf(TARGET_YEAR);
  const cumulativeNetCost = idx2045 >= 0 ? cumulativeNetCostSeries[idx2045].value : cumulative;

  // NPV
  let netPresentValue = 0;
  for (let i = 0; i < years.length; i++) {
    const t = years[i] - BASE_YEAR;
    netPresentValue -= annualCapexSeries[i].value / Math.pow(1 + a.discountRate, t);
    netPresentValue += annualSavingsSeries[i].value / Math.pow(1 + a.discountRate, t);
  }

  // Payback: first year cumulative net cost <= 0 after we've had positive cumulative (real investment to recover).
  // Avoid "payback 2023" when year-1 has 0 capex/0 savings (policy ramp starts later).
  let paybackYear: number | null = null;
  if (cumulativeNetCost <= 0 && upfrontCapitalCost > 0) {
    let seenPositive = false;
    for (let i = 0; i < cumulativeNetCostSeries.length; i++) {
      const v = cumulativeNetCostSeries[i].value;
      if (v > 0) seenPositive = true;
      if (v <= 0 && seenPositive) {
        paybackYear = cumulativeNetCostSeries[i].year;
        break;
      }
    }
  }

  const totalBuildingEmissionsReductionMT = 0; // Caller overrides from emissions data
  const costPerTon = totalBuildingEmissionsReductionMT > 0 ? cumulativeNetCost / totalBuildingEmissionsReductionMT : null;

  const electrificationCapex = electrificationCapexByYear.reduce((a, b) => a + b, 0);
  const reachCapex = reachCapexByYear.reduce((a, b) => a + b, 0);
  const residentialRetrofitCapex = resRetroCapexByYear.reduce((a, b) => a + b, 0);
  const commercialRetrofitCapex = comRetroCapexByYear.reduce((a, b) => a + b, 0);

  const baselineGasCostByYear = years.map((y, i) => ({ year: y, value: baselineTherms[i] * a.naturalGasPricePerTherm }));
  const baselineElecCostByYear = years.map((y, i) => ({ year: y, value: baselineKwh[i] * a.electricityPricePerKwh }));
  const policyGasCostByYear = years.map((y, i) => ({ year: y, value: policyTherms[i] * a.naturalGasPricePerTherm }));
  const policyElecCostByYear = years.map((y, i) => ({ year: y, value: policyKwh[i] * a.electricityPricePerKwh }));

  const baselineGasCostTotal = baselineGasCostByYear.reduce((s, r) => s + r.value, 0);
  const policyGasCostTotal = policyGasCostByYear.reduce((s, r) => s + r.value, 0);
  const policyElecCostTotal = policyElecCostByYear.reduce((s, r) => s + r.value, 0);
  const incrementalOperatingCostTotal = policyCostByYear.reduce((s, _, i) => s + (policyCostByYear[i] - baselineCostByYear[i]), 0);
  const electricityAddedKwhTotal = policyKwh.reduce((s, pk, i) => s + Math.max(0, pk - baselineKwh[i]), 0);

  // Naive $/therm equivalent for HP at COP=3 (same convention as prior sanity note): elec $/kWh × (29.3/COP) ≈ $/therm of heat delivered
  const KWH_PER_THERM_REF = 29.3;
  const copRefOperatingDebug = 3.0;
  const electricEquivalentDollarsPerTherm =
    a.electricityPricePerKwh * (KWH_PER_THERM_REF / copRefOperatingDebug);

  const operatingCostDebug = electrificationContext?.hasElectrification
    ? {
        baselineGasCostTotal,
        policyGasCostTotal,
        policyElecCostTotal,
        incrementalOperatingCostTotal,
        electricityAddedKwhTotal,
        effectiveDollarsPerThermElectric: electricEquivalentDollarsPerTherm,
      }
    : undefined;

  return {
    upfrontCapitalCost,
    annualSavingsSeries,
    annualCapexSeries,
    cumulativeNetCostSeries,
    cumulativeNetCost,
    netPresentValue,
    costPerTon,
    paybackYear,
    totalBuildingEmissionsReductionMT,
    baselineCostByYear: years.map((y, i) => ({ year: y, value: baselineCostByYear[i] })),
    policyCostByYear: years.map((y, i) => ({ year: y, value: policyCostByYear[i] })),
    baselineGasCostByYear,
    baselineElecCostByYear,
    policyGasCostByYear,
    policyElecCostByYear,
    electrificationCapex,
    reachCapex,
    residentialRetrofitCapex,
    commercialRetrofitCapex,
    electrificationDebug: electrificationDebugResult,
    operatingCostDebug,
    electrificationStock2045,
  };
}

// ============== TRANSPORT ==============

export interface TransportPathway {
  years: number[];
  vmtByYear: number[];
  evShareByYear: number[]; // 0–1
  /** EV charging kWh by year from emissions pipeline; when set, used for cost (ensures consistency with GHG forecast). */
  evChargingKwhByYear?: number[];
}

export interface TransportPathwayCostResult {
  upfrontCapitalCost: number;
  annualFuelSavingsSeries: { year: number; value: number }[];
  cumulativeNetCostSeries: { year: number; value: number }[];
  cumulativeNetCost: number;
  netPresentValue: number;
  emissionsReductionMT: number;
  costPerTon: number | null;
  paybackYear: number | null;
  breakdown: { vehicleCapex: number; chargingCost: number; fuelSavings: number };
}

/**
 * Compute transport cost between two pathways using a single formula:
 * - Operating: baseline fuel cost − policy fuel cost (gallons × gasPrice + kWh × elecPrice)
 * - CAPEX: incremental new EVs per year × (vehicle cost + charger cost)
 * - Annual net cost = capex − fuel savings; then cumulative, NPV, payback.
 */
export function computeTransportCostBetweenPathways(
  baselinePathway: TransportPathway,
  policyPathway: TransportPathway,
  options?: TransportCostOptions
): TransportPathwayCostResult {
  const gasolinePrice = options?.gasolinePricePerGallon ?? GASOLINE_PRICE_PER_GALLON_DEFAULT;
  const electricityPrice = options?.electricityPricePerKwh ?? 0.25;
  const discountRate = options?.discountRate ?? TRANSPORT_DISCOUNT_RATE;
  const iceMpg = Math.max(10, Math.min(60, options?.iceMpg ?? ICE_MPG));
  const evKwhPerMile = Math.max(0.2, Math.min(0.5, options?.evKwhPerMile ?? EV_KWH_PER_MILE));
  const evIncrementalCost = Math.max(0, options?.evIncrementalCost ?? EV_INCREMENTAL_COST);
  const chargerCostPerEv = Math.max(0, options?.chargerCostPerEv ?? CHARGER_COST_PER_EV);

  const years = baselinePathway.years.filter((y) => policyPathway.years.includes(y));
  if (years.length === 0) {
    return {
      upfrontCapitalCost: 0,
      annualFuelSavingsSeries: [],
      cumulativeNetCostSeries: [],
      cumulativeNetCost: 0,
      netPresentValue: 0,
      emissionsReductionMT: 0,
      costPerTon: null,
      paybackYear: null,
      breakdown: { vehicleCapex: 0, chargingCost: 0, fuelSavings: 0 },
    };
  }

  const baselineVmt = years.map((y) => baselinePathway.vmtByYear[baselinePathway.years.indexOf(y)] ?? 0);
  const baselineEvShare = years.map((y) => baselinePathway.evShareByYear[baselinePathway.years.indexOf(y)] ?? 0);
  const policyVmt = years.map((y) => policyPathway.vmtByYear[policyPathway.years.indexOf(y)] ?? 0);
  const policyEvShare = years.map((y) => policyPathway.evShareByYear[policyPathway.years.indexOf(y)] ?? 0);
  const baselineEvKwh = baselinePathway.evChargingKwhByYear;
  const policyEvKwh = policyPathway.evChargingKwhByYear;

  // A. Operating costs (fuel); use evChargingKwh from emissions pipeline when available for consistency
  const baselineCostByYear = years.map((_, i) => {
    const gal = baselineVmt[i] * (1 - baselineEvShare[i]) / iceMpg;
    const kwh = baselineEvKwh?.[i] ?? baselineVmt[i] * baselineEvShare[i] * evKwhPerMile;
    return gal * gasolinePrice + kwh * electricityPrice;
  });
  const policyCostByYear = years.map((_, i) => {
    const gal = policyVmt[i] * (1 - policyEvShare[i]) / iceMpg;
    const kwh = policyEvKwh?.[i] ?? policyVmt[i] * policyEvShare[i] * evKwhPerMile;
    return gal * gasolinePrice + kwh * electricityPrice;
  });
  const annualFuelSavingsSeries = years.map((y, i) => ({
    year: y,
    value: baselineCostByYear[i] - policyCostByYear[i],
  }));

  // B. CAPEX: incremental new EVs per year × (vehicle + charger cost)
  const vehicleCountByYear = policyVmt.map((v) => v / AVG_MILES_PER_VEHICLE_PER_YEAR);
  const deltaEVCountByYear = years.map((_, i) => {
    const now = (policyEvShare[i] - baselineEvShare[i]) * vehicleCountByYear[i];
    if (i === 0) return Math.max(0, now);
    const prev = (policyEvShare[i - 1] - baselineEvShare[i - 1]) * (policyVmt[i - 1] / AVG_MILES_PER_VEHICLE_PER_YEAR);
    return Math.max(0, now - prev);
  });
  const annualVehicleCapex = deltaEVCountByYear.map((n) => n * evIncrementalCost);
  const annualChargingCapex = deltaEVCountByYear.map((n) => n * chargerCostPerEv);
  const annualCapexSeries = years.map((y, i) => ({
    year: y,
    value: annualVehicleCapex[i] + annualChargingCapex[i],
  }));
  const upfrontCapitalCost = annualCapexSeries.reduce((sum, row) => sum + row.value, 0);

  // C. Cumulative net cost, NPV, payback
  let cumulative = 0;
  const cumulativeNetCostSeries = years.map((y, i) => {
    const netCost = annualCapexSeries[i].value - annualFuelSavingsSeries[i].value;
    cumulative += netCost;
    return { year: y, value: cumulative };
  });
  const idx2045 = years.indexOf(ANALYSIS_YEAR_MAX);
  const cumulativeNetCost = idx2045 >= 0 ? cumulativeNetCostSeries[idx2045].value : cumulative;

  let netPresentValue = 0;
  for (let i = 0; i < years.length; i++) {
    const t = years[i] - ANALYSIS_YEAR_MIN;
    netPresentValue += annualFuelSavingsSeries[i].value / Math.pow(1 + discountRate, t);
    netPresentValue -= annualCapexSeries[i].value / Math.pow(1 + discountRate, t);
  }

  // Payback = first year cumulative ≤ 0 after we've had positive cumulative (real investment to recover).
  // Avoid "payback 2023" when year-1 has 0 capex/0 savings (policy ramp starts in 2024).
  let paybackYear: number | null = null;
  if (cumulativeNetCost <= 0 && upfrontCapitalCost > 0) {
    let seenPositive = false;
    for (let i = 0; i < cumulativeNetCostSeries.length; i++) {
      const v = cumulativeNetCostSeries[i].value;
      if (v > 0) seenPositive = true;
      if (v <= 0 && seenPositive) {
        paybackYear = cumulativeNetCostSeries[i].year;
        break;
      }
    }
  }

  const fuelSavings = annualFuelSavingsSeries.reduce((s, r) => s + r.value, 0);
  const vehicleCapex = annualVehicleCapex.reduce((a, b) => a + b, 0);
  const chargingCost = annualChargingCapex.reduce((a, b) => a + b, 0);

  // Emissions reduction: from LDV emissions if caller provides; here we leave 0 and costPerTon null
  const emissionsReductionMT = 0;
  const costPerTon = emissionsReductionMT > 0 && Number.isFinite(cumulativeNetCost)
    ? cumulativeNetCost / emissionsReductionMT
    : null;

  return {
    upfrontCapitalCost,
    annualFuelSavingsSeries,
    cumulativeNetCostSeries,
    cumulativeNetCost,
    netPresentValue,
    emissionsReductionMT,
    costPerTon,
    paybackYear,
    breakdown: { vehicleCapex, chargingCost, fuelSavings },
  };
}
